/**
 * What a timeline row was showing: classify a recorded path by route, and pull
 * out the ids that name its subject — the question, paper, sitting, attempt or
 * student on screen.
 *
 * A route table rather than per-page knowledge, so a new page is one row here.
 * The first matching rule wins, so a specific route sits above the general one
 * it would otherwise fall into (`/question-sets/:id/student/:sid/results/:sess`
 * above `/question-sets/:id`).
 *
 * The ids are resolved in one batch by POST /console/user-action-refs.
 *
 * See docs/FeatureDesigns/user-actions.md.
 */

export type PageRefType = 'question' | 'questionSet' | 'session' | 'attempt' | 'user';

export interface PageRef {
  type: PageRefType;
  id: string;
}

export interface PageClass {
  /** Human name for the kind of page, e.g. "Question", "Paper sitting". */
  category: string;
  refs: PageRef[];
}

/** One path segment — an id never contains a slash, query or fragment. */
const SEG = '([^/?#]+)';

interface Rule {
  pattern: RegExp;
  category: string;
  /** Capture groups in order → what each one is. `null` skips a group. */
  groups: (PageRefType | null)[];
}

const rule = (source: string, category: string, groups: (PageRefType | null)[] = []): Rule => ({
  // Anchored on both ends; teacher routes may be recorded with or without the
  // leading slash, and a query string or fragment may follow.
  pattern: new RegExp(`^/?${source}/?(?:[?#].*)?$`),
  category,
  groups,
});

const RULES: Rule[] = [
  // ── Student app ──
  rule(`question/${SEG}`, 'Question', ['question']),
  rule(`attempts/${SEG}`, 'Attempt', ['attempt']),
  rule(`concept-lab/${SEG}`, 'Concept lab', ['attempt']),
  rule(`session/${SEG}`, 'Paper sitting', ['session']),
  rule(`results/${SEG}`, 'Paper results', ['session']),
  rule(`exam/${SEG}/instructions`, 'Exam instructions', ['questionSet']),
  rule(`(?:bulk-)?upload/${SEG}`, 'Upload', [null]),
  rule(`feedback/${SEG}`, 'Feedback', [null]),
  rule(`learning-moments/${SEG}`, 'Learning moment', [null]),
  rule(`revise/${SEG}`, 'Concept revision', [null]),
  rule(`battle/(?!friends|leaderboard)${SEG}`, 'Duel', [null]),
  rule(`practice/subject/${SEG}/${SEG}/q/${SEG}`, 'Chapter practice', [null, null, null]),
  rule(`practice/subject/${SEG}(?:/${SEG})?`, 'Chapter practice', [null, null]),

  // ── Teacher app ──
  rule(`students/${SEG}/attempts/${SEG}`, 'Student attempt', ['user', 'attempt']),
  rule(`students/${SEG}/sessions/${SEG}/questions/${SEG}`, 'Student question', ['user', 'session', 'question']),
  rule(`students/${SEG}(?:/.*)?`, 'Student', ['user']),
  rule(`(?:assignments|question-sets)/${SEG}/student/${SEG}/results/${SEG}`, 'Student results', ['questionSet', 'user', 'session']),
  rule(`(?:assignments|question-sets)/${SEG}/student/${SEG}(?:/upload)?`, 'Student on paper', ['questionSet', 'user']),
  rule(`assignments/question/${SEG}`, 'Question', ['question']),
  rule(`hint-quality/${SEG}`, 'Hint quality', ['question']),

  // ── Both ──
  rule(`(?:assignments|question-sets)/(?!create)${SEG}(?:/[a-z-]+)?`, 'Paper', ['questionSet']),
];

/** List pages that open a question in place via `?q=<questionId>`. */
const QUESTION_QUERY_PAGES = /^\/?(?:question-sets|questions|my-questions)(?:[/?#]|$)/;

export function classifyPage(path: string): PageClass | null {
  const refs: PageRef[] = [];
  let category: string | null = null;

  const bare = path.split(/[?#]/)[0];
  for (const r of RULES) {
    const m = r.pattern.exec(bare);
    if (!m) continue;
    category = r.category;
    r.groups.forEach((type, i) => {
      const id = m[i + 1];
      if (type && id && !id.startsWith(':')) refs.push({ type, id: decodeURIComponent(id) });
    });
    break;
  }

  const q = path.includes('?') ? new URLSearchParams(path.slice(path.indexOf('?') + 1)).get('q') : null;
  if (q && QUESTION_QUERY_PAGES.test(path)) {
    refs.push({ type: 'question', id: q });
    category ??= 'Question';
  }

  return category ? { category, refs } : null;
}

/** Stable key for a ref, used to dedupe and to look up its resolution. */
export const refKey = (r: PageRef): string => `${r.type}:${r.id}`;
